// skillgenpro-frontend/src/error-framework/contextResolver.js
//
// Resolves "what is the user doing right now" so error messages can be
// context-aware instead of generic (Phase 3/4 of the framework spec).
//
// Deliberately lightweight for this first (Payment + AI) rollout: rather
// than coupling to every existing Context provider in the app (UserContext,
// TenantContext, SubscriptionContext, etc. — whose exact shapes weren't all
// read as part of this pass), it reads the same localStorage keys
// axiosInterceptor.js already relies on for identity, plus the current
// route. This keeps the framework's core decoupled from any one provider's
// internals; a provider-based resolver can replace this later without
// changing errorMapper/recoveryEngine/ErrorExperience, since they only ever
// see the plain `{ feature, page, role }` object this produces.

function safeParseUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

/**
 * @param {string} pathname - current route, e.g. from useLocation().pathname
 * @param {Object} [overrides] - explicit feature/aiSubject context a
 *   component already knows better than any route-based guess (e.g. a
 *   payment component can pass `{ feature: 'checkout' }` directly)
 */
export function resolveContext(pathname, overrides) {
  overrides = overrides || {};
  const user = safeParseUser();

  let feature = overrides.feature || inferFeatureFromPath(pathname);
  let role = overrides.role || (user && (user.role || user.userType)) || 'unknown';

  return {
    page: pathname || (typeof window !== 'undefined' ? window.location.pathname : ''),
    feature,
    role,
    ...overrides,
  };
}

function inferFeatureFromPath(pathname) {
  if (!pathname) return 'unknown';
  if (pathname.includes('/pricing') || pathname.includes('/checkout') || pathname.includes('/subscription')) return 'checkout';
  if (pathname.includes('/nextgen-builder')) return 'nextgen-builder';
  if (pathname.includes('/interview')) return 'mock-interview';
  if (pathname.includes('/bootcamp')) return 'bootcamp';
  if (pathname.includes('/assessment') || pathname.includes('/challenge')) return 'assessment';
  if (pathname.includes('/college')) return 'college-dashboard';
  if (pathname.includes('/admin')) return 'admin-dashboard';
  if (pathname.includes('/chat') || pathname.includes('/tutor')) return 'ai-tutor';
  return 'general';
}
